import { apiUrl } from '../utils/api';

export interface RegisterEntry {
  id?: string;
  customerId: string;
  projectId: string;
  type: string;
  data?: Record<string, unknown>;
  createdAt?: string;
  updatedAt?: string;
}

export interface FactData {
  statement: string;
  category: string;
  status: 'confirmed' | 'assumed' | 'unknown';
  source: string;
  confirmedBy?: string;
  confirmedAt?: string;
  blocking: boolean;
  resolution?: string;
  relatedWorkloadIds: string[];
}

export interface WorkloadData {
  name: string;
  type: string;
  currentEnvironment: string;
  migrationStrategy: string;
  priority: string;
  wave: number;
  dependencies: string[];
  targetAzureSku?: string;
  estimatedMonthlyCost?: number;
  status: string;
  components: string[];
  targetService?: string;
  targetSpec?: string;
  sqlDependency: boolean;
  userCount?: number;
  region?: string;
}

export interface DecisionData {
  topic: string;
  context: string;
  options: string[];
  decision: string;
  rationale: string;
  consequences: string;
  date: string;
  decider: string;
  status: string;
  linkedDocumentId?: string;
}

export interface RiskData {
  description: string;
  category: string;
  likelihood: number;
  impact: number;
  riskScore: number;
  mitigation: string;
  owner: string;
  status: string;
  linkedWorkloadId?: string;
}

export type RegisterType = 'fact' | 'workload' | 'decision' | 'risk' | 'stakeholder' | 'skill';

export async function listRegisters(
  customerId: string, projectId: string, type: RegisterType, token: string
): Promise<RegisterEntry[]> {
  const params = new URLSearchParams({ customerId, projectId, type });
  const res = await fetch(apiUrl(`/api/registers?${params}`), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Failed to list registers: ${res.status}`);
  return res.json();
}

export async function createRegister(entry: RegisterEntry, token: string): Promise<RegisterEntry> {
  const res = await fetch(apiUrl('/api/registers'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(entry),
  });
  if (!res.ok) throw new Error(`Failed to create register: ${res.status}`);
  return res.json();
}

export async function updateRegister(id: string, entry: RegisterEntry, token: string): Promise<RegisterEntry> {
  const res = await fetch(apiUrl(`/api/registers/${id}`), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(entry),
  });
  if (!res.ok) throw new Error(`Failed to update register: ${res.status}`);
  return res.json();
}

export async function deleteRegister(id: string, customerId: string, token: string): Promise<void> {
  const res = await fetch(apiUrl(`/api/registers/${id}?customerId=${encodeURIComponent(customerId)}`), {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Failed to delete register: ${res.status}`);
}
